import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { provisionUser } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import { getRedirectResult } from 'firebase/auth';
import {
  Box, Button, Typography, CircularProgress, Alert, Paper,
} from '@mui/material';
import MicrosoftIcon from '@mui/icons-material/Window';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';

export default function Login() {
  const { currentUser, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
      return;
    }

    getRedirectResult(auth).then(async result => {
      if (result?.user) {
        const provision = await provisionUser(result.user);
        if (provision.allowed === false) {
          setError(provision.reason);
        } else {
          navigate('/dashboard');
        }
      }
    }).catch(err => {
      console.error(err);
    }).finally(() => {
      setCheckingRedirect(false);
    });
  }, [currentUser, navigate]);

  async function handleLogin() {
    setLoading(true);
    setError('');
    try {
      const result = await loginWithMicrosoft();
      if (!result.success) {
        setError(result.error || 'Inloggning misslyckades. Forsok igen.');
      }
    } catch (err) {
      setError('Inloggning misslyckades. Forsok igen.');
    } finally {
      setLoading(false);
    }
  }

  if (checkingRedirect) return (
    <Box sx={{ display: 'flex', justifyContent: 'center',
      alignItems: 'center', minHeight: '100vh', bgcolor: '#0a0a0a' }}>
      <CircularProgress sx={{ color: '#FF6B00' }} />
    </Box>
  );

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#0a0a0a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 2, p: 2
    }}>
      <Paper sx={{
        p: 5, maxWidth: 400, width: '100%', textAlign: 'center',
        bgcolor: '#1a1a1a', borderRadius: 3,
        border: '1px solid rgba(255,255,255,0.08)'
      }}>
        <Typography fontWeight={900} sx={{
          fontSize: '2rem', color: 'white', mb: 0.5,
          fontFamily: '"Segoe UI", sans-serif'
        }}>
          Lagbas<Box component="span" sx={{ color: '#FF6B00' }}>N</Box>
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
          AI-driven konsultmatchning
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {/* Microsoft-knapp (intern personal) */}
        <Button
          fullWidth variant="contained" size="large"
          onClick={handleLogin} disabled={loading}
          startIcon={loading
            ? <CircularProgress size={20} color="inherit" />
            : <MicrosoftIcon />}
          sx={{
            bgcolor: '#FF6B00', color: 'white', py: 1.5,
            fontSize: '1rem', fontWeight: 600, borderRadius: 2,
            '&:hover': { bgcolor: '#e65c00' },
            '&:disabled': { bgcolor: '#333', color: '#666' }
          }}>
          {loading ? 'Loggar in...' : 'Logga in med Microsoft 365'}
        </Button>

        <Typography variant="caption" sx={{
          color: '#444', display: 'block', mt: 2 }}>
          Endast behöriga e-postdomäner har tillgång.
          Kontakta din administratör om du inte kommer in.
        </Typography>
      </Paper>

      {/* GDPR-info under login-kortet */}
      <Box sx={{
        maxWidth: 400, width: '100%',
        display: 'flex', alignItems: 'flex-start', gap: 1,
        px: 1
      }}>
        <GppMaybeIcon sx={{ color: '#444', fontSize: 16, mt: 0.3,
          flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: '#444', lineHeight: 1.6 }}>
          LagbasN behandlar personuppgifter i enlighet med GDPR.
          Vid inloggning visas information om personuppgiftsbehandlingen
          och du ombeds lamna ditt samtycke. Data lagras inom EU och
          delas inte med tredje part.
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ color: '#333', mt: 3 }}>
        Build {process.env.REACT_APP_VERSION || require('../../package.json').version}
      </Typography>
    </Box>
  );
}